import { HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, share } from 'rxjs/operators';

import {
  AppAnalyticsCommand,
  AppleApplicationsService,
  ApplicationAttributesCommand,
  ApplicationConfigurationSetting,
  BluetoothCommand,
  CommandProgress,
  ConfigurationProfile,
  DataRoamingCommand,
  DeviceNameCommand,
  DiagnosticSubmissionCommand,
  HostNameCommand,
  ManagedApplicationConfigurationCommand,
  MaximumResidentUsersCommand,
  MdmCommandService,
  MDMOptionsCommand,
  PassCodeLockGracePeriodCommand,
  PersonalHotspotCommand,
  VoiceRoamingCommand,
} from '@rocksoft/mdm-sdk-v2';
import { ErrorModel, isNotNullOrUndefined } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';
import { Wallpaper } from '../../../mdm/models/wallpaper.model';
import { AlternativeMdmSdk } from '../../apple/alternative-mdm-sdk';

@Injectable({
  providedIn: 'root',
})
export class AppleMdmService {
  private readonly api = inject(MdmCommandService);
  private readonly alternativeApi = inject(AlternativeMdmSdk);
  private readonly applicationService = inject(AppleApplicationsService);
  private readonly toastService = inject(ToastService);

  lockDevice(udid: string, pin: string, message: string, phoneNumber: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueDeviceLockCommand({
        udid,
        apiVersion: '2.0',
        deviceLockCommand: { pin, message, phoneNumber },
      }),
      'An error occurred while locking the device'
    );
  }

  restartDevice(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueRestartDeviceCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while restarting the device'
    );
  }

  shutdownDevice(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueShutdownDeviceCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while shutting down the device'
    );
  }

  getDeviceInformation(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueDeviceInformationAllCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while refreshing device information'
    );
  }

  clearPasscode(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueClearPasscodeCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while clearing the passcode'
    );
  }

  enableLostMode(udid: string, message: string, phoneNumber: string, footnote: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueEnableLostModeCommand({
        udid,
        apiVersion: '2.0',
        enableLostModeCommand: { message, phoneNumber, footnote },
      }),
      'An error occurred while enabling lost mode'
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- this is most likely a bug which needs to be investigated.
  manageSetting(udid: string, settings: [{}]): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueManagedSettingsCommand({
        udid,
        apiVersion: '2.0',
        managedSettingsCommand: { settings },
      }),
      'An error occurred while updating managed settings'
    );
  }

  playLostModeSound(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queuePlayLostModeSoundCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while playing lost mode sound'
    );
  }

  disableLostMode(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueDisableLostModeCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while disabling lost mode'
    );
  }

  deviceLocation(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueDeviceLocationCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while requesting device location'
    );
  }

  eraseDevice(udid: string, pin: string, preserveDataPlan: boolean): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueEraseDeviceCommand({
        udid,
        apiVersion: '2.0',
        eraseDeviceCommandDto: { pin, preserveDataPlan },
      }),
      'An error occurred while erasing the device'
    );
  }

  getProfiles(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueProfileListCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while refreshing profiles'
    );
  }

  getCertificates(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueCertificatesCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while refreshing certificates'
    );
  }

  getOsUpdates(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueAvailableOSUpdatesCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while refreshing OS updates'
    );
  }

  getOsUpdateStates(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueOSUpdateStatusCommand({
        udid,
        apiVersion: '2.0',
      }),
      'An error occurred while refreshing OS update states'
    );
  }

  getApps(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueInstalledApplicationListCommand({
        udid,
        apiVersion: '2.0',
        installedApplicationListCommand: {},
      }),
      'An error occurred while refreshing installed applications'
    );
  }

  getManagedApplications(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueManagedApplicationListCommand({
        udid,
        apiVersion: '2.0',
        managedApplicationListCommand: {},
      }),
      'An error occurred while refreshing managed applications'
    );
  }

  installProfile(udid: string, profile: ConfigurationProfile): Observable<CommandProgress> {
    profile.udid = udid;
    return this.withErrorToast(
      this.api.queueInstallProfileCommand({
        udid,
        apiVersion: '2.0',
        configurationProfileInstallProfileCommand: { payload: profile },
      }),
      'An error occurred while installing the profile'
    );
  }

  installProfileAsFile(): Observable<CommandProgress> {
    throw new Error('Method not implemented.'); // TODO
  }

  removeProfile(udid: string, profileUuid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueRemoveProfileCommand({
        udid,
        apiVersion: '2.0',
        removeProfileCommand: { identifier: profileUuid },
      }),
      'An error occurred while removing the profile'
    );
  }

  removeApp(udid: string, identifier: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueRemoveApplicationCommand({
        udid,
        apiVersion: '2.0',
        removeApplicationCommand: { identifier },
      }),
      'An error occurred while removing the application'
    );
  }

  installApp(udid: string, manifestURL: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueInstallApplicationCommand({
        udid,
        apiVersion: '2.0',
        installApplicationCommand: { manifestURL },
      }),
      'An error occurred while installing the application'
    );
  }

  installVppApplication(udid: string, adamIdStr: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.applicationService.getVppAssets({ apiVersion: '2.0' }).pipe(
        mergeMap((apiVppAssets) => apiVppAssets.filter((vppAsset) => vppAsset.adamIdStr === adamIdStr)),
        mergeMap((vppAsset) =>
          this.api.queueInstallApplicationCommand({
            udid,
            apiVersion: '2.0',
            installApplicationCommand: {
              iTunesStoreID: parseInt(vppAsset.adamIdStr),
              managementFlags: 1,
              changeManagementState: 'Managed',
              options: { purchaseMethod: 1 },
            },
          })
        )
      ),
      'An error occurred while installing the VPP application'
    );
  }

  installManualApp(file: Blob, udid: string): Observable<number | CommandProgress> {
    return this.withErrorToast(
      this.alternativeApi.queueInstallApplicationManualCommandForm(file, udid, undefined, 'events', true).pipe(
        map((e) => {
          if (e.type === HttpEventType.UploadProgress) {
            return e.total != null ? Math.round((e.loaded / e.total) * 100) : 0;
          }

          if (e.type === HttpEventType.Response) {
            return e.body;
          }
        }),
        filter(isNotNullOrUndefined),
        share()
      ),
      'An error occurred while installing the manual application'
    );
  }

  installKnownApp(udid: string, bundleIdentifier: string, bundleVersion: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.applicationService.getApplications({ apiVersion: '2.0' }).pipe(
        mergeMap((apiAppleApps) =>
          apiAppleApps.filter(
            (appleApp) => appleApp.bundleIdentifier === bundleIdentifier && appleApp.bundleVersion === bundleVersion
          )
        ),
        mergeMap((appleApp) =>
          this.api.queueInstallApplicationCommand({
            udid,
            apiVersion: '2.0',
            installApplicationCommand: {
              manifestURL: appleApp.manifestUrl,
              managementFlags: 1,
              changeManagementState: 'Managed',
            },
          })
        )
      ),
      'An error occurred while installing the known application'
    );
  }

  installEnterpriseApp(udid: string, manifestURL: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueInstallEnterpriseApplicationCommand({
        udid,
        apiVersion: '2.0',
        installEnterpriseApplicationCommand: { manifestURL },
      }),
      'An error occurred while installing the enterprise application'
    );
  }

  getSecurityInfo(udid: string): Observable<CommandProgress> {
    return this.withErrorToast(
      this.api.queueSecurityInfoCommand({ udid, apiVersion: '2.0' }),
      'An error occurred while refreshing the security info'
    );
  }

  saveApplicationConfiguration(udid: string, configuration: ApplicationConfigurationSetting) {
    return this.withErrorToast(
      this.api.queueApplicationConfigurationCommand({
        udid,
        apiVersion: '2.0',
        applicationConfigurationSetting: configuration,
      }),
      'An error occurred while saving application configuration'
    );
  }

  getManagedApplicationConfigurations(udid: string) {
    const command: ManagedApplicationConfigurationCommand = {};
    return this.withErrorToast(
      this.api.queueManagedApplicationConfigurationCommand({
        udid,
        apiVersion: '2.0',
        managedApplicationConfigurationCommand: command,
      }),
      'An error occurred while refreshing managed application configurations'
    );
  }

  /*------------------------------***Managed Settings***------------------------------*/

  /*Shared iPad Mode only. Sets the user preference of sharing analytics with app developers.*/
  appAnalytics(udid: string, enabled: boolean) {
    const analytics: AppAnalyticsCommand = { enabled };
    return this.withErrorToast(
      this.api.queueAppAnalyticsCommand({ udid, apiVersion: '2.0', appAnalyticsCommand: analytics }),
      'An error occurred while applying app analytics settings'
    );
  }

  applicationAttributes(udid: string, identifier: string, vpnuuid: string) {
    const command: ApplicationAttributesCommand = {
      identifier,
      attributes: { vpnuuid },
    };
    return this.withErrorToast(
      this.api.queueApplicationAttributesCommand({
        udid,
        apiVersion: '2.0',
        applicationAttributesCommand: command,
      }),
      'An error occurred while applying application attributes'
    );
  }

  bluetooth(udid: string, enabled: boolean) {
    const command: BluetoothCommand = { enabled };
    return this.withErrorToast(
      this.api.queueBluetoothCommand({ udid, apiVersion: '2.0', bluetoothCommand: command }),
      'An error occurred while applying bluetooth settings'
    );
  }

  /*Requires an LTE supported iPad.
    Enabling data roaming also enables voice roaming.*/
  dataRoaming(udid: string, enabled: boolean) {
    const command: DataRoamingCommand = { enabled };
    return this.withErrorToast(
      this.api.queueDataRoamingCommand({ udid, apiVersion: '2.0', dataRoamingCommand: command }),
      'An error occurred while applying data roaming settings'
    );
  }

  deviceName(udid: string, deviceName: string) {
    const command: DeviceNameCommand = { deviceName };
    return this.withErrorToast(
      this.api.queueDeviceNameCommand({ udid, apiVersion: '2.0', deviceNameCommand: command }),
      'An error occurred while applying device name settings'
    );
  }

  /*Shared iPad Mode only.Sets the user preference of diagnostic submission.*/
  diagnosticSubmission(udid: string, enabled: boolean) {
    const command: DiagnosticSubmissionCommand = { enabled };
    return this.withErrorToast(
      this.api.queueDiagnosticSubmissionCommand({ udid, apiVersion: '2.0', diagnosticSubmissionCommand: command }),
      'An error occurred while applying diagnostic submission settings'
    );
  }

  /*HostName of the device(available only on macOS 10.11 or later).*/
  hostName(udid: string, hostName: string) {
    const command: HostNameCommand = { hostName };
    return this.withErrorToast(
      this.api.queueHostNameCommand({ udid, apiVersion: '2.0', hostNameCommand: command }),
      'An error occurred while applying host name settings'
    );
  }

  /*Shared iPad Mode only. Sets the maximum number of users that can use a Shared iPad.*/
  maximumResidentUsers(udid: string, maximumResidentUsers: number) {
    const command: MaximumResidentUsersCommand = { maximumResidentUsers };
    return this.withErrorToast(
      this.api.queueMaximumResidentUsersCommand({ udid, apiVersion: '2.0', maximumResidentUsersCommand: command }),
      'An error occurred while applying maximum resident users settings'
    );
  }

  mdmOptions(udid: string, activationLockAllowedWhileSupervised?: boolean) {
    const command: MDMOptionsCommand = { mdmOptions: { activationLockAllowedWhileSupervised } };
    return this.withErrorToast(
      this.api.queueMDMOptionsCommand({ udid, apiVersion: '2.0', mDMOptionsCommand: command }),
      'An error occurred while applying MDM options'
    );
  }

  /*Shared iPad Mode only. Minimum value is 0. Maximum value is 14400.*/
  passCodeLockGracePeriod(udid: string, passCodeLockGracePeriod: number) {
    const command: PassCodeLockGracePeriodCommand = { passCodeLockGracePeriod };
    return this.withErrorToast(
      this.api.queuePassCodeLockGracePeriodCommand({
        udid,
        apiVersion: '2.0',
        passCodeLockGracePeriodCommand: command,
      }),
      'An error occurred while applying passcode lock grace period settings'
    );
  }

  /*Requires an iPad with LTE support.*/
  personalHotspot(udid: string, enabled: boolean) {
    const command: PersonalHotspotCommand = { enabled };
    return this.withErrorToast(
      this.api.queuePersonalHotspotCommand({ udid, apiVersion: '2.0', personalHotspotCommand: command }),
      'An error occurred while applying personal hotspot settings'
    );
  }

  /*Gets or Sets where the wallpaper should be applied.
  'where' is either:
  1: Lock screen.
  2: Home screen.
  3: Lock and Home screen.*/
  setWallpaper(wallpaper: Wallpaper, udid: string) {
    return this.withErrorToast(
      this.alternativeApi.queueSetWallpaperCommandForm(udid, wallpaper.image, wallpaper.where),
      'An error occurred while setting wallpaper'
    );
  }

  /*The voice roaming setting is only available on certain carriers.
  Disabling voice roaming also disables data roaming.*/
  voiceRoaming(udid: string, enabled: boolean) {
    const command: VoiceRoamingCommand = { enabled };
    return this.withErrorToast(
      this.api.queueVoiceRoamingCommand({ udid, apiVersion: '2.0', voiceRoamingCommand: command }),
      'An error occurred while applying voice roaming settings'
    );
  }

  private getErrorDetail(e: ErrorModel): string {
    return e?.error?.error?.message ?? e?.error?.message ?? e?.message ?? e?.status?.toString() ?? 'Unknown error';
  }

  private withErrorToast<T>(source$: Observable<T>, errorMessage: string): Observable<T> {
    return source$.pipe(
      catchError((error) => {
        const detailMessage = this.getErrorDetail(error);
        this.toastService.showError(`${errorMessage}: ${detailMessage}`);
        return throwError(() => error);
      })
    );
  }
}
